import { Injectable } from '@angular/core';
import { IFormGroup } from '@shared-libs/interfaces';
import { Action, DigibankObjective, NavigationDataKey, NavigationType, UserPermissions } from '@shared-libs/enums';
import { formIsValid, translate } from '@shared-libs/helpers';
import { IReservation } from '@internal-models/reservation.model';
import { ReservationFormFactory } from '@internal-factories/forms/reservation-form.factory';
import { NavigationDataManager } from '@shared-managers/navigation-data.manager';
import { ReservationProvider } from '@internal-providers/reservation.provider';
import { NavigationService } from '@shared-services/navigation.service';
import { EventAttendanceType, EventLocationType, IEvent } from '@internal-models/event.model';
import { ICalendarEvent } from '@shared-models/calendar-event.model';
import { ILoan } from '@internal-models/loan.model';
import { LoanComponent } from '@internal-components/device-components/loan-component/loan.component';
import { IQuestionCategory } from '@internal-models/question-category.model';
import { QuestionCategoryProvider } from '@internal-providers/question-category.provider';
import { FormControl, FormGroup } from '@angular/forms';
import { InternalNavigationType } from '@shared-directives/navigate.directive';
import { AttendanceStatus } from '@internal-models/attendance.model';
import { EventProvider } from '@internal-providers/event.provider';
import { UserManager } from '@shared-managers/user.manager';
import { ClientProvider } from '@internal-providers/client.provider';
import { IClient } from '@internal-models/client.model';
import { IWaitListItem } from '@internal-models/wait-list-item.model';

@Injectable({ providedIn: 'root' })
export class ReservationController {
	public reservationForm: IFormGroup<IReservation>;
	public reservation: IReservation;
	public reservations: Array<IReservation>;
	public calendarEvent: ICalendarEvent;
	public action: Action;
	public isInPast: boolean;
	public isToday: boolean;
	public isFromWaitList: boolean;
	public type: EventAttendanceType;
	public event: IEvent;
	public questionCategories: Array<IQuestionCategory>;
	public communication: Array<{ control: FormControl; label: string }>;
	public digibankObjectives: Array<{ value: DigibankObjective; label: string }>;
	public attendancesVisible: boolean;
	public showConfirmation: boolean = false;
	public clientLocked: boolean = false;
	public reservationLocked: boolean = false;

	public readonly Action: typeof Action = Action;
	public readonly EventAttendanceType: typeof EventAttendanceType = EventAttendanceType;
	public readonly InternalNavigationType: typeof InternalNavigationType = InternalNavigationType;
	public readonly NavigationDataKey: typeof NavigationDataKey = NavigationDataKey;
	public readonly AttendanceStatus: typeof AttendanceStatus = AttendanceStatus;
	public readonly EventLocationType: typeof EventLocationType = EventLocationType;
	public readonly UserPermissions: typeof UserPermissions = UserPermissions;

	constructor(
		public readonly navigationDataManager: NavigationDataManager,
		public readonly navigationService: NavigationService,
		public readonly reservationProvider: ReservationProvider,
		private readonly questionCategoryProvider: QuestionCategoryProvider,
		private readonly eventProvider: EventProvider,
		private readonly userManager: UserManager,
		private readonly clientProvider: ClientProvider
	) {}

	public async onInit(): Promise<void> {
		this.reservation = this.navigationDataManager.getData(NavigationDataKey.reservation);
		this.reservationLocked = this.navigationDataManager.getData(NavigationDataKey.action) === Action.SELECT;
		this.action = this.reservation?.Client ? Action.UPDATE : Action.CREATE;
		this.type = this.navigationDataManager.getData(NavigationDataKey.eventAttendanceType);
		this.calendarEvent = this.navigationDataManager.getData<ICalendarEvent>(NavigationDataKey.calendarEvent);
		const waitListItem = this.navigationDataManager.getData<IWaitListItem>(NavigationDataKey.waitListItem);
		this.isInPast = (this.reservation?.endsAt ?? this.calendarEvent.endsAt) <= new Date().toISOString();
		this.isToday =
			new Date(this.reservation?.startsAt ?? this.calendarEvent.startsAt).toDateString() ==
			new Date().toDateString();
		this.attendancesVisible = this.isToday || this.isInPast;

		if (this.calendarEvent.Event.id) {
			this.event = await this.eventProvider.getOne(this.calendarEvent.Event.id);
		}

		const form = this.navigationDataManager.getData<IFormGroup<IReservation>>(NavigationDataKey.reservationForm);

		if (form) {
			this.reservationForm = form;
			if (this.reservationForm.get('Client').value.id) {
				let client: IClient = await this.clientProvider.getOne(this.reservationForm.get('Client').value.id);
				client.birthDate = new Date(client.birthDate) as any;
				this.reservationForm.get('Client').patchValue(client);
				this.reservationForm = ReservationFormFactory.create(
					this.reservationForm.value,
					Action.CREATE,
					this.isInPast
				);
				this.clientLocked = true;
			}
		} else {
			this.reservationForm = ReservationFormFactory.create(
				this.reservation,
				this.action,
				this.isInPast,
				waitListItem
			);
			if (waitListItem) {
				this.isFromWaitList = true;
				this.clientLocked = true;
			}
			if (!this.reservation?.replyTo && this.event.replyTo) {
				this.reservationForm.get('replyTo').patchValue(this.event.replyTo);
			}
			if (!this.reservation?.telephone && this.event.telephone) {
				this.reservationForm.get('telephone').patchValue(this.event.telephone);
			}
			if (!this.reservation?.digibankObjective && this.event.digibankObjective) {
				this.reservationForm.get('digibankObjective').patchValue(this.event.digibankObjective);
			}
		}

		this.getDigibankObjectives();
		this.getCommunicationItems();
		void this.getQuestionCategories();

		this.reservations = await this.getReservations();
	}

	public onDestroy(): void {
		this.reservationForm = undefined;
		this.isFromWaitList = undefined;
		this.showConfirmation = false;
		this.clientLocked = false;
	}

	public async reserve(): Promise<void> {
		if (formIsValid(this.reservationForm)) {
			const reservationForm = ReservationFormFactory.getValue(this.reservationForm) as IReservation;
			this.reservation = await this.reservationProvider.reserve(reservationForm.id, reservationForm);
			this.showConfirmation = true;
		}
	}

	public async register(): Promise<void> {
		if (formIsValid(this.reservationForm)) {
			const reservationForm = {
				...ReservationFormFactory.getValue(this.reservationForm),
				CalendarEventId: this.calendarEvent.id,
				EventId: this.calendarEvent.Event.id,
			} as IReservation;

			if (this.type === EventAttendanceType.WalkIn) {
				this.reservation = await this.reservationProvider.registerWalkIn(reservationForm);
			} else if (this.type === EventAttendanceType.Lessons) {
				this.reservation = await this.reservationProvider.registerForLesson(reservationForm);
			} else {
				this.reservation = await this.reservationProvider.createRegistration(reservationForm);
			}

			this.showConfirmation = true;
		}
	}

	public async updateReservation(): Promise<void> {
		if (formIsValid(this.reservationForm)) {
			const reservationForm = {
				...ReservationFormFactory.getValue(this.reservationForm),
				EventId: this.calendarEvent.Event.id,
			} as IReservation;
			this.reservation = await this.reservationProvider.update(this.reservation.id, reservationForm);
			this.showConfirmation = true;
		}
	}

	public useNavigationDataManager() {
		this.navigationDataManager.addData(NavigationDataKey.reservationForm, this.reservationForm);
	}

	public async openLoan(loan?: ILoan): Promise<void> {
		void this.navigationService
			.navigateTo<{ action: Action }>(NavigationType.modal, LoanComponent, {
				loan,
				client: this.reservation.Client,
			})
			.then((result) => {
				if (result?.action) {
				}
			})
			.catch();
	}

	public close(): void {
		this.navigationService.navigateBack({ event: this.calendarEvent.Event, calendarEvent: this.calendarEvent });
	}

	private async getReservations(): Promise<Array<IReservation>> {
		if ((this.action === Action.UPDATE || this.isFromWaitList) && this.type === EventAttendanceType.Reservation) {
			return this.reservationProvider.getAllOfEventDay(
				this.reservation.id,
				this.calendarEvent.Event.id,
				this.calendarEvent.id
			);
		}
	}

	private async getQuestionCategories(): Promise<void> {
		this.questionCategories = await this.questionCategoryProvider.getAll();
	}

	private getCommunicationItems(): void {
		this.communication = Object.keys((this.reservationForm.get('Communication') as FormGroup).controls).map(
			(key) => ({
				control: (this.reservationForm.get('Communication') as FormGroup).controls[key] as FormControl,
				label: translate(`COMMUNICATION.${key.toUpperCase()}`, { email: '' }),
			})
		);
	}

	/**
	 * Get the getDigibankObjectives options
	 * formatted for the form-select
	 */
	private getDigibankObjectives(): void {
		this.digibankObjectives = Object.keys(DigibankObjective)
			.filter((key) => DigibankObjective[key] !== DigibankObjective.Loan)
			.map((key) => ({
				value: DigibankObjective[key],
				label: translate(`EVENT.DIGIBANK_OBJECTIVE.${key.toUpperCase()}`),
			}));
	}
}
